/**
 * EstimateOutreachTab.tsx — Billing Phase 1E
 * Espejo trimmed de ProjectOutreachTab.tsx: sin composer manual, sin
 * clientEvents (billing no tiene sistema de interaction tokens). Feed ASC.
 */
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import {
  useEstimateDrafts,
  useEstimateReplies,
  type EstimateOutreachDraft,
  type EstimateOutreachReply,
} from '../../hooks/useEstimateOutreach.js';
import { EstimateDraftCardConnected } from './EstimateDraftCardConnected.js';
import { EstimateReplyCardConnected } from './EstimateReplyCardConnected.js';
import type { Estimate } from '../../hooks/useBilling.js';

type FeedItem =
  | { kind: 'draft'; ts: number; item: EstimateOutreachDraft }
  | { kind: 'reply'; ts: number; item: EstimateOutreachReply };

interface EstimateOutreachTabProps {
  estimate: Estimate;
}

export function EstimateOutreachTab({ estimate }: EstimateOutreachTabProps) {
  const { t } = useTranslation();
  const estimateId = estimate.id;

  const { data: draftsData, isLoading: draftsLoading } = useEstimateDrafts(estimateId);
  const { data: repliesData, isLoading: repliesLoading } = useEstimateReplies(estimateId);

  const isLoading = draftsLoading || repliesLoading;

  const feed: FeedItem[] = [
    ...(draftsData?.drafts ?? []).map(d => ({ kind: 'draft' as const, ts: d.sentAt ?? d.createdAt, item: d })),
    ...(repliesData?.replies ?? []).map(r => ({ kind: 'reply' as const, ts: r.receivedAt, item: r })),
  ].sort((a, b) => a.ts - b.ts);

  const isEmpty = !isLoading && feed.length === 0;

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      <DetailSection title={t('billing.outreach.title')} divider={false}>
        {isLoading && <LoadingState />}
        {isEmpty && <EmptyState title={t('billing.outreach.empty')} />}
        {feed.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {feed.map(item =>
              item.kind === 'draft'
                ? <EstimateDraftCardConnected key={`d-${item.item.id}`} draft={item.item} estimateId={estimateId} />
                : <EstimateReplyCardConnected key={`r-${item.item.id}`} reply={item.item} estimateId={estimateId} />,
            )}
          </div>
        )}
      </DetailSection>
    </div>
  );
}
